import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Form, Modal } from "react-bootstrap";
import Select from "react-select";
import { getToolOptions, saveTool, Tool } from "../../api/tooling";
import { ErrorNotice } from "./ToolingShared";

export default function ToolForm({
  tool,
  onClose,
}: {
  tool?: Tool;
  onClose: () => void;
}) {
  const client = useQueryClient();
  const options = useQuery({
    queryKey: ["tooling", "options"],
    queryFn: getToolOptions,
  });
  const [type, setType] = useState(tool?.type || "compound");
  const [operation, setOperation] = useState(tool?.operation || "cutting");
  const [details, setDetails] = useState(tool?.details || "");
  const [parts, setParts] = useState(tool?.parts.map((p) => p.part_id) || []);
  const [locationId, setLocationId] = useState<number | null>(
    tool?.location_id ?? null
  );
  const locationOptions = options.data?.locations || [];
  const selectedLocation =
    locationOptions.find((location) => location.value === locationId) ||
    (tool?.location?.value === locationId ? tool.location : null);
  const mutation = useMutation({
    mutationFn: () =>
      saveTool(
        { type, operation, part_ids: parts, details, location_id: locationId },
        tool?.id
      ),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["tooling"] });
      onClose();
    },
  });
  const partOptions = [
    ...(options.data?.parts || []),
    ...(tool?.parts
      .filter((p) => !options.data?.parts.some((o) => o.value === p.part_id))
      .map((p) => ({
        value: p.part_id,
        label: `${[p.part_name, p.drawing_name, p.part_id]
          .filter(
            (value) => value !== null && value !== undefined && value !== ""
          )
          .join(" / ")} (unavailable)`,
      })) || []),
  ];
  return (
    <Modal
      show
      onHide={() => !mutation.isLoading && onClose()}
      size="lg"
      backdrop="static"
    >
      <Modal.Header closeButton>
        <Modal.Title>{tool ? `Edit ${tool.code}` : "Add Tooling"}</Modal.Title>
      </Modal.Header>
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate();
        }}
      >
        <Modal.Body>
          <ErrorNotice error={options.error} retry={() => options.refetch()} />
          <ErrorNotice error={mutation.error} />
          <Form.Group className="mb-3" controlId="tool-type">
            <Form.Label>Type</Form.Label>
            <Form.Select value={type} onChange={(e) => setType(e.target.value)}>
              {options.data?.types.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3" controlId="tool-parts">
            <Form.Label>Linked parts</Form.Label>
            <Select
              inputId="tool-parts"
              placeholder="Search by part name, drawing number or ID…"
              isMulti
              options={partOptions}
              value={parts
                .map((id) => partOptions.find((o) => o.value === id))
                .filter((o): o is { value: number; label: string } => !!o)}
              onChange={(items) => setParts(items.map((i) => i.value))}
            />
            <Form.Text>
              The first selected part labels this tool on the board. Remove and
              reselect to change the order.
            </Form.Text>
          </Form.Group>
          <Form.Group className="mb-3" controlId="tool-location">
            <Form.Label>Location (optional)</Form.Label>
            <Select
              inputId="tool-location"
              placeholder="Search locations..."
              isSearchable
              isClearable
              isLoading={options.isLoading}
              options={locationOptions}
              value={selectedLocation}
              onChange={(location) => setLocationId(location?.value ?? null)}
            />
          </Form.Group>
          <Form.Group controlId="tool-operation">
            <Form.Label>Operation</Form.Label>
            <Form.Select
              value={operation}
              onChange={(e) => setOperation(e.target.value)}
            >
              <option value="cutting">Cutting</option>
              <option value="bending">Bending</option>
            </Form.Select>
          </Form.Group>
          <p className="text-muted small mt-3">
            Codes are assigned automatically by the server, with a separate
            sequence for each tool type (for example, B-001, G-001, G-002).
            Changing the type assigns the next code for that type; other edits
            keep the existing code.
          </p>
          <Form.Group controlId="tool-details">
            <Form.Label>Details</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              maxLength={10000}
              value={details}
              onChange={(e) => setDetails(e.target.value)}
            />
          </Form.Group>
          {tool && (
            <Form.Text>
              Changing tooling details or linked parts requires fresh approval
              for its cards.
            </Form.Text>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            disabled={mutation.isLoading}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={mutation.isLoading || !options.data || !parts.length}
          >
            {mutation.isLoading ? "Saving…" : "Save tooling"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
